import type { Participant } from "../types/messaging.types.ts"

/**
 * A mention travels as `@[uuid]` so the identity survives a rename, and the
 * display name is resolved at render time from the conversation's own roster.
 * Storing the name in the text would leave stale names in old messages and
 * would let anyone type a name that looks like a mention.
 */
export const MENTION_MARKER = /@\[([0-9a-fA-F-]{36})\]/g

export type MessageSegment =
  | { kind: "text"; value: string }
  | { kind: "mention"; userId: string; label: string }
  | { kind: "link"; href: string; label: string }

const URL_PATTERN = /(https?:\/\/[^\s<]+)/g

const linkSegments = (text: string): MessageSegment[] => {
  const segments: MessageSegment[] = []
  let cursor = 0
  for (const match of text.matchAll(URL_PATTERN)) {
    const start = match.index ?? 0
    if (start > cursor) {
      segments.push({ kind: "text", value: text.slice(cursor, start) })
    }
    // A trailing sentence mark is punctuation, not part of the address.
    const raw = match[0].replace(/[.,;:!?)\]]+$/, "")
    segments.push({ kind: "link", href: raw, label: raw })
    cursor = start + raw.length
  }
  if (cursor < text.length) {
    segments.push({ kind: "text", value: text.slice(cursor) })
  }
  return segments
}

export const toSegments = (
  body: string,
  participants: Participant[],
  /** Names carried by the message itself; authoritative over the roster. */
  names: Record<string, string> = {}
): MessageSegment[] => {
  const byId = new Map(participants.map((person) => [person.id, person]))
  const segments: MessageSegment[] = []
  let cursor = 0

  for (const match of body.matchAll(MENTION_MARKER)) {
    const start = match.index ?? 0
    if (start > cursor) {
      segments.push(...linkSegments(body.slice(cursor, start)))
    }
    const resolved = names[match[1]] ?? byId.get(match[1])?.full_name
    segments.push({
      kind: "mention",
      userId: match[1],
      // Someone who has since left the conversation still has to render as
      // their name — the mention happened, and "@unknown" reads as a bug.
      // Never the raw token, and never "unknown" — the mention happened,
      // the person has simply gone.
      label: `@${resolved ?? "Former participant"}`
    })
    cursor = start + match[0].length
  }
  if (cursor < body.length) {
    segments.push(...linkSegments(body.slice(cursor)))
  }
  return segments
}

/** A message as one line of plain text: every marker replaced by a name. */
export const toDisplayText = (
  body: string,
  participants: Participant[],
  names: Record<string, string> = {}
): string =>
  toSegments(body, participants, names)
    .map((segment) => (segment.kind === "text" ? segment.value : segment.label))
    .join("")

export interface MentionQuery {
  /** Index of the `@` that opened the query. */
  start: number
  term: string
}

/**
 * The partial `@name` under the caret, or null when the caret is not in one.
 * A mention opens at a word boundary only, so an email address never does.
 */
export const mentionQueryAt = (
  text: string,
  caret: number
): MentionQuery | null => {
  const upToCaret = text.slice(0, caret)
  const at = upToCaret.lastIndexOf("@")
  if (at === -1) return null
  if (at > 0 && !/\s/.test(upToCaret[at - 1])) return null
  const term = upToCaret.slice(at + 1)
  if (/[\s[\]]/.test(term)) return null
  return { start: at, term }
}

export const applyMention = (
  text: string,
  query: MentionQuery,
  caret: number,
  person: Participant
): { text: string; caret: number } => {
  const marker = `@[${person.id}] `
  const next = text.slice(0, query.start) + marker + text.slice(caret)
  return { text: next, caret: query.start + marker.length }
}

/**
 * A mention the composer is holding: where its name sits in the visible text,
 * and who it means.
 *
 * Identity is kept against a *range*, not against the name, because two people
 * can share a name and one person can be mentioned twice. Re-deriving from the
 * text alone would have to guess; this never does.
 */
export interface ComposerMention {
  start: number
  end: number
  userId: string
  label: string
}

export interface ComposerState {
  /** What the person sees: `@Mike Davis`, never `@[uuid]`. */
  text: string
  mentions: ComposerMention[]
}

/** Marker body → what the composer shows, with the ranges that carry identity. */
export const toComposerState = (
  body: string,
  participants: Participant[],
  names: Record<string, string> = {}
): ComposerState => {
  const byId = new Map(participants.map((person) => [person.id, person]))
  const mentions: ComposerMention[] = []
  let text = ""
  let cursor = 0

  for (const match of body.matchAll(MENTION_MARKER)) {
    const start = match.index ?? 0
    text += body.slice(cursor, start)
    const resolved = names[match[1]] ?? byId.get(match[1])?.full_name
    // Somebody who has left the conversation still has to read as a person,
    // and the raw token must never surface.
    const label = `@${resolved ?? "Former participant"}`
    mentions.push({
      start: text.length,
      end: text.length + label.length,
      userId: match[1],
      label
    })
    text += label
    cursor = start + match[0].length
  }
  text += body.slice(cursor)
  return { text, mentions }
}

/** Only the mentions whose label still sits exactly where it was. */
export const survivingMentions = (
  text: string,
  mentions: ComposerMention[]
): ComposerMention[] =>
  mentions.filter(
    (mention) => text.slice(mention.start, mention.end) === mention.label
  )

/** What the composer shows → the marker body the server stores. */
export const toMarkerBody = (
  text: string,
  mentions: ComposerMention[]
): string => {
  let body = text
  // Right to left, so an earlier replacement cannot move a later range.
  for (const mention of survivingMentions(text, mentions).sort(
    (a, b) => b.start - a.start
  )) {
    body = body.slice(0, mention.start) + `@[${mention.userId}]` + body.slice(mention.end)
  }
  return body
}

/** Insert a person's name at the query, keeping their id against that range. */
export const applyMentionToText = (
  state: ComposerState,
  query: MentionQuery,
  caret: number,
  person: Participant
): { state: ComposerState; caret: number } => {
  const label = `@${person.full_name}`
  const inserted = `${label} `
  const text =
    state.text.slice(0, query.start) + inserted + state.text.slice(caret)
  const shift = inserted.length - (caret - query.start)

  const moved = survivingMentions(state.text, state.mentions).map((mention) =>
    mention.start >= caret
      ? { ...mention, start: mention.start + shift, end: mention.end + shift }
      : mention
  )

  return {
    state: {
      text,
      mentions: [
        ...moved,
        {
          start: query.start,
          end: query.start + label.length,
          userId: person.id,
          label
        }
      ]
    },
    caret: query.start + inserted.length
  }
}
